import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Icon, Skeleton, Tag, Typography } from '@ann/web-components';
import {
  WidgetGrid,
  WidgetGridProvider,
  WidgetCardShell,
} from '../../../../../components/WidgetGrid';
import ActivityListWidget from '../../../../../components/WidgetGrid/customWidgets/ucpWidgets/ActivityListWidget';
import NotesListWidget from '../../../../../components/WidgetGrid/customWidgets/ucpWidgets/NotesListWidget';
import Widget from '../../../../../components/WidgetGrid/components/Widget/Widget';
import {
  InlineEditableText,
  LocationPreviewCard,
  SelectLocationModal,
  ShareDashboardModal,
  WidgetLibraryModal,
} from '../../components';
import { DASHBOARD_STATES } from '../../constants';
import { DASHBOARD_EDITOR_COPY } from '../../constants/dashboardBuilderCopy';
import WIDGET_PREVIEW_DATA from '../../constants/widgetPreviewData';
import { useDashboardEditor } from '../../hooks';
import { adaptWidgetsForGrid } from '../../utils/widgetAdapter';
import {
  AddWidgetRow,
  CanvasWrapper,
  Container,
  EmptyStateCard,
  EmptyStateContent,
  EmptyStateDescription,
  EmptyStateDescriptionWrap,
  EmptyStateTitle,
  Header,
  HeaderLeft,
  HeaderRight,
  IconHighlight,
  LocationPill,
  SaveIconWrapper,
  TitleBlock,
  TitleRow,
} from './DashboardNew.styled';

const PREVIEW_REGISTRY = {
  activity: ActivityListWidget,
  notes: NotesListWidget,
};

const DashboardNewSkeleton = () => (
  <Container>
    <Skeleton height="56px" width="100%" borderRadius="8px" />
    <Skeleton height="44px" width="130px" borderRadius="8px" />
    <Skeleton height="223px" width="100%" borderRadius="8px" />
    <Skeleton height="223px" width="100%" borderRadius="8px" />
  </Container>
);

const DashboardNew = () => {
  const editor = useDashboardEditor();
  const navigate = useNavigate();

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isWidgetLibraryModalOpen, setIsWidgetLibraryModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [publishPending, setPublishPending] = useState(false);
  const [toast, setToast] = useState(null);
  const {
    addWidgets,
    canAddWidgets,
    isEditing,
    publish,
    description,
    id,
    loading,
    removeWidget,
    saveDraft,
    saving,
    sectionId,
    sectionPath,
    state,
    title,
    toggleWidgetLock,
    updateEditor,
    updateLayouts,
    widgets,
    sharedWith,
  } = editor;

  const handleBack = () => {
    navigate('/dashboards/');
  };

  const showToast = useCallback((severity, title, description = '', options = {}) => {
    setToast({
      severity,
      title,
      description,
      autoClose: options.autoClose ?? true,
      closable: options.closable ?? true,
    });
  }, []);

  const openLocationModal = useCallback(() => setIsLocationModalOpen(true), []);
  const closeLocationModal = useCallback(() => setIsLocationModalOpen(false), []);
  const openWidgetLibraryModal = useCallback(() => setIsWidgetLibraryModalOpen(true), []);
  const closeWidgetLibraryModal = useCallback(() => setIsWidgetLibraryModalOpen(false), []);
  const openShareModal = useCallback(() => setIsShareModalOpen(true), []);
  const closeShareModal = useCallback(() => setIsShareModalOpen(false), []);
  const closeToast = useCallback(() => setToast(null), []);
  const getValidationMessage = useCallback(
    (validationErrors = []) =>
      validationErrors
        .map((errorKey) =>
          errorKey === 'title'
            ? DASHBOARD_EDITOR_COPY.toast.validationNoTitle
            : DASHBOARD_EDITOR_COPY.toast.validationNoSection
        )
        .join(' '),
    []
  );

  const hasWidgets = widgets.length > 0;
  const statusLabel = state === DASHBOARD_STATES.PUBLISHED ? 'Published' : 'Draft';

  const handleSaveDraft = useCallback(async () => {
    if (saving) return;
    showToast(
      'warning',
      DASHBOARD_EDITOR_COPY.toast.draftSaving,
      DASHBOARD_EDITOR_COPY.toast.draftSavingDescription,
      { autoClose: false }
    );
    const result = await saveDraft();
    if (!result.success) {
      showToast(
        'error',
        DASHBOARD_EDITOR_COPY.toast.draftError,
        DASHBOARD_EDITOR_COPY.toast.draftErrorDescription
      );
      return;
    }
    if (result.redirected) {
      return;
    }
    showToast(
      'success',
      DASHBOARD_EDITOR_COPY.toast.draftSaved,
      DASHBOARD_EDITOR_COPY.toast.draftSavedDescription
    );
  }, [saveDraft, saving, showToast]);

  const handlePublish = useCallback(async () => {
    if (saving) return;
    const hasTitle = Boolean(String(title || '').trim());
    const hasSection = Boolean(sectionId);
    if (!hasTitle || !hasSection) {
      const message = [
        !hasTitle ? DASHBOARD_EDITOR_COPY.toast.validationNoTitle : null,
        !hasSection ? DASHBOARD_EDITOR_COPY.toast.validationNoSection : null,
      ]
        .filter(Boolean)
        .join(' ');
      showToast('error', DASHBOARD_EDITOR_COPY.toast.validationTitle, message);
      return;
    }

    if (!Array.isArray(sharedWith) || sharedWith.length === 0) {
      setPublishPending(true);
      openShareModal();
      return;
    }

    showToast(
      'warning',
      DASHBOARD_EDITOR_COPY.toast.publishing,
      DASHBOARD_EDITOR_COPY.toast.publishingDescription,
      { autoClose: false }
    );
    const result = await publish();

    if (result?.validationErrors?.length) {
      const message = getValidationMessage(result.validationErrors);
      showToast('error', DASHBOARD_EDITOR_COPY.toast.validationTitle, message);
      return;
    }

    if (result?.needsShare) {
      setPublishPending(true);
      setToast(null);
      openShareModal();
      return;
    }

    if (!result?.success) {
      showToast(
        'error',
        DASHBOARD_EDITOR_COPY.toast.publishError,
        DASHBOARD_EDITOR_COPY.toast.publishErrorDescription
      );
      return;
    }

  }, [getValidationMessage, openShareModal, publish, saving, sectionId, sharedWith, showToast, title]);

  const handleLocationSelect = useCallback((selection) => {
    if (!selection) return;
    updateEditor({
      sectionId: selection.id || null,
      sectionPath: selection.path || '',
    });
    showToast(
      'success',
      DASHBOARD_EDITOR_COPY.location.alertTitle,
      DASHBOARD_EDITOR_COPY.location.alertDescription
    );
    closeLocationModal();
  }, [closeLocationModal, showToast, updateEditor]);

  const handleWidgetsAdd = useCallback((selectedWidgets) => {
    addWidgets(selectedWidgets);
    closeWidgetLibraryModal();
  }, [addWidgets, closeWidgetLibraryModal]);

  const handleShareConfirm = useCallback(async (sharedWith) => {
    if (saving) return;
    const resolvedSharedWith = Array.isArray(sharedWith) ? sharedWith : [];
    updateEditor({ sharedWith: resolvedSharedWith });
    closeShareModal();
    if (!publishPending) {
      showToast(
        'success',
        DASHBOARD_EDITOR_COPY.toast.shared,
        DASHBOARD_EDITOR_COPY.toast.sharedDescription
      );
      return;
    }

    setPublishPending(false);
    showToast(
      'warning',
      DASHBOARD_EDITOR_COPY.toast.publishing,
      DASHBOARD_EDITOR_COPY.toast.publishingDescription,
      { autoClose: false }
    );
    const result = await publish({ sharedWith: resolvedSharedWith });
    if (result?.validationErrors?.length) {
      const message = getValidationMessage(result.validationErrors);
      showToast('error', DASHBOARD_EDITOR_COPY.toast.validationTitle, message);
      return;
    }
    if (!result?.success) {
      showToast(
        'error',
        DASHBOARD_EDITOR_COPY.toast.publishError,
        DASHBOARD_EDITOR_COPY.toast.publishErrorDescription
      );
      return;
    }
  }, [closeShareModal, getValidationMessage, publish, publishPending, saving, showToast, updateEditor]);

  const handleTitleChange = useCallback((nextTitle) => {
    updateEditor({ title: nextTitle });
  }, [updateEditor]);

  const handleDescriptionChange = useCallback((nextDescription) => {
    updateEditor({ description: nextDescription });
  }, [updateEditor]);

  const externalWidgetConfig = useMemo(
    () => adaptWidgetsForGrid(widgets),
    [widgets],
  );

  const renderWidget = useCallback((widgetConfig) => {
    const builderType = widgetConfig._builderType;
    const previewData = WIDGET_PREVIEW_DATA[builderType] ?? null;
    const cardConfig = widgetConfig.config?.card || {};
    const WidgetComponent = PREVIEW_REGISTRY[builderType];

    let content = null;
    if (previewData && WidgetComponent) {
      content = <WidgetComponent data={previewData} isCompressed={false} />;
    } else if (previewData) {
      content = <Widget data={previewData} />;
    }

    return (
      <WidgetCardShell
        title={cardConfig.title}
        headerButtons={cardConfig.header_buttons}
        isPinned={widgetConfig._locked}
        onPin={() => toggleWidgetLock(widgetConfig.instance_uuid)}
        onDelete={() => removeWidget(widgetConfig.instance_uuid)}
      >
        {content}
      </WidgetCardShell>
    );
  }, [removeWidget, toggleWidgetLock]);

  if (loading) {
    return <DashboardNewSkeleton />;
  }

  return (
    <>
      <Container>
        <Header>
          <HeaderLeft>
            <Button
              type="tertiary"
              iconLeft="arrowLeft"
              size="small"
              onClick={handleBack}
            />
            <TitleBlock>
              <TitleRow>
                <InlineEditableText
                  variant="title"
                  value={title}
                  placeholder="Untitled template"
                  onChange={handleTitleChange}
                />
                <Tag
                  text={statusLabel}
                  color={state === DASHBOARD_STATES.PUBLISHED ? 'success' : 'neutral'}
                  icon={state === DASHBOARD_STATES.PUBLISHED ? 'check' : undefined}
                  size="small"
                />
                {sectionPath && (
                  <LocationPill type="button" onClick={openLocationModal}>
                    <Icon name="locationOn" size="small" color="neutral" />
                    <Typography category="body" size="small" weight="regular" color="neutral">
                      {sectionPath}
                    </Typography>
                  </LocationPill>
                )}
              </TitleRow>
              <InlineEditableText
                variant="description"
                value={description}
                placeholder="Add description..."
                onChange={handleDescriptionChange}
              />
            </TitleBlock>
          </HeaderLeft>

          <HeaderRight>
            <Button
              type="tertiary"
              size="small"
              iconLeft="share"
              onClick={openShareModal}
            />
            <SaveIconWrapper onClick={handleSaveDraft}>
              <Icon materialName="save" size="small" />
            </SaveIconWrapper>
            <Button
              type="primary"
              size="small"
              label={saving ? 'Publishing...' : 'Publish'}
              disabled={saving}
              onClick={handlePublish}
            />
          </HeaderRight>
        </Header>

        <AddWidgetRow>
          <Button
            type="secondary"
            size="small"
            iconLeft="plus"
            label="Add Widget"
            disabled={!canAddWidgets}
            onClick={openWidgetLibraryModal}
          />
        </AddWidgetRow>

        {!sectionPath && (
          <EmptyStateCard>
            <EmptyStateContent>
              <IconHighlight>
                <Icon name="locationOn" size="medium" color="primary-alt" />
              </IconHighlight>
              <EmptyStateTitle>Choose section</EmptyStateTitle>
              <EmptyStateDescriptionWrap>
                <EmptyStateDescription>
                  The widgets you can add depend on where this dashboard will be displayed.
                </EmptyStateDescription>
              </EmptyStateDescriptionWrap>
            </EmptyStateContent>
            <Button
              type="primary"
              size="medium"
              label="Choose"
              iconRight="plus"
              onClick={openLocationModal}
            />
          </EmptyStateCard>
        )}

        {sectionPath && !isEditing && <LocationPreviewCard />}

        {hasWidgets ? (
          <CanvasWrapper>
            <WidgetGridProvider isEditMode>
              <WidgetGrid
                externalWidgetConfig={externalWidgetConfig}
                onExternalLayoutChange={updateLayouts}
                renderWidget={renderWidget}
              />
            </WidgetGridProvider>
          </CanvasWrapper>
        ) : (
          <EmptyStateCard $fill={Boolean(sectionPath)}>
            <EmptyStateContent>
              <IconHighlight>
                <Icon name="grid" size="medium" color="primary-alt"/>
              </IconHighlight>
              <EmptyStateTitle>{DASHBOARD_EDITOR_COPY.emptyState.title}</EmptyStateTitle>
              <EmptyStateDescriptionWrap>
                <EmptyStateDescription>
                  {DASHBOARD_EDITOR_COPY.emptyState.description}
                </EmptyStateDescription>
              </EmptyStateDescriptionWrap>
            </EmptyStateContent>
            <Button
              type="primary"
              size="medium"
              label={DASHBOARD_EDITOR_COPY.emptyState.addButton}
              iconRight="plus"
              disabled={!canAddWidgets}
              onClick={openWidgetLibraryModal}
            />
          </EmptyStateCard>
        )}
      </Container>

      <SelectLocationModal
        open={isLocationModalOpen}
        onClose={closeLocationModal}
        onSelect={handleLocationSelect}
      />
      <WidgetLibraryModal
        open={isWidgetLibraryModalOpen}
        onClose={closeWidgetLibraryModal}
        onConfirm={handleWidgetsAdd}
        sectionId={sectionId}
      />
      <ShareDashboardModal
        open={isShareModalOpen}
        onClose={closeShareModal}
        onConfirm={handleShareConfirm}
        dashboardId={id || 'new'}
        dashboardName={title}
        existingSharedWith={sharedWith}
        existingAccessCount={sharedWith.length}
      />

      {toast && (
          <Alert
            severity={toast.severity}
            title={toast.title}
            description={toast.description}
            zIndex={1300}
            closable={toast.closable}
            autoClose={toast.autoClose}
            autoCloseDelay={4000}
            onClose={closeToast}
            maxWidth="692px"
          />
      )}
    </>
  );
};

export default DashboardNew;
